import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Trash2, UserX } from "lucide-react";
import { deleteUserByEmail } from "@/utils/adminUserDeletion";
import { Separator } from "@/components/ui/separator";

export function ListingDeleter() {
  const [listingId, setListingId] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const deleteListing = async () => {
    if (!listingId.trim()) {
      toast({
        title: "Error",
        description: "Please enter a listing ID",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-delete-listing', {
        body: { listing_id: listingId.trim() },
      });

      if (error) throw error;

      if (data?.success) {
        toast({
          title: "Success",
          description: "Listing deleted successfully",
        });
        setListingId("");
      } else {
        throw new Error("Failed to delete listing");
      }
    } catch (error: any) {
      console.error('Error deleting listing:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete listing",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async () => {
    if (!userEmail.trim()) {
      toast({
        title: "Error",
        description: "Please enter a user email",
        variant: "destructive",
      });
      return;
    }

    const confirmed = confirm(
      `Are you sure you want to delete the user: ${userEmail}?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    setLoading(true);
    try {
      const result = await deleteUserByEmail(userEmail.trim());
      toast({
        title: "Success",
        description: result.message || `User ${userEmail} deleted successfully`,
      });
      setUserEmail("");
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete user",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Delete Listing / User (Admin)</CardTitle>
        <CardDescription>
          Permanently delete listings or user accounts
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h3 className="text-sm font-medium mb-2">Delete Listing</h3>
          <div className="flex gap-2">
            <Input
              placeholder="Listing ID"
              value={listingId}
              onChange={(e) => setListingId(e.target.value)}
              disabled={loading}
            />
            <Button 
              onClick={deleteListing} 
              disabled={loading || !listingId.trim()}
              variant="destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
          <div className="text-sm text-muted-foreground mt-2">
            <p>Listing IDs to delete:</p>
            <ul className="list-disc list-inside mt-2">
              <li>442b0a45-8bdd-4a2a-bd13-6c7671753406</li>
              <li>fca66dd8-b2df-4494-9c17-7f04291491ba</li>
            </ul>
          </div>
        </div>

        <Separator />

        <div>
          <h3 className="text-sm font-medium mb-2">Delete User</h3>
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="user@example.com"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              disabled={loading}
            />
            <Button 
              onClick={deleteUser} 
              disabled={loading || !userEmail.trim()}
              variant="destructive"
            >
              <UserX className="h-4 w-4 mr-2" />
              Delete User
            </Button>
          </div>
          <div className="text-sm text-muted-foreground mt-2">
            <p>Enter glennmctag1@pbsbusiness.com.au to wipe that signup</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
